'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import supabase from '@/supabase';
import { DirectMessage, DirectMessageWithSender } from '@/types/chat';
import { RealtimeChannel, RealtimePostgresInsertPayload } from '@supabase/supabase-js';

interface UseRealtimeDirectMessagesProps {
  conversationId: string;
  userId: string;
  enabled?: boolean;
}

interface UseRealtimeDirectMessagesReturn {
  messages: DirectMessageWithSender[];
  isConnected: boolean;
  isLoading: boolean;
  error: string | null;
  sendMessage: (content: string, attachmentUrl?: string, attachmentType?: DirectMessage['attachment_type']) => Promise<{ success: boolean; error?: string }>;
  loadMoreMessages: (cursor: string) => Promise<void>;
  hasMoreMessages: boolean;
}

const PAGE_SIZE = 50;

export function useRealtimeDirectMessages({
  conversationId,
  userId,
  enabled = true,
}: UseRealtimeDirectMessagesProps): UseRealtimeDirectMessagesReturn {
  const [messages, setMessages] = useState<DirectMessageWithSender[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const channelRef = useRef<RealtimeChannel | null>(null);

  // Fetch initial messages
  useEffect(() => {
    if (!conversationId || !userId || !enabled) {
      setMessages([]);
      setIsLoading(false);
      return;
    }

    const fetchMessages = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const { data, error: fetchError } = await supabase
          .from('direct_messages')
          .select(`
            *,
            sender:profiles!direct_messages_sender_id_fkey(
              id,
              display_name,
              avatar_url
            )
          `)
          .eq('conversation_id', conversationId)
          .order('created_at', { ascending: false })
          .order('id', { ascending: false })
          .limit(PAGE_SIZE);

        if (fetchError) {
          console.error('[useRealtimeDirectMessages] Fetch error:', fetchError);
          setError('Failed to load messages');
          return;
        }

        // Reverse to show oldest first
        const messagesWithSender = (data || []).reverse() as DirectMessageWithSender[];
        setMessages(messagesWithSender);
        setHasMoreMessages((data?.length || 0) >= PAGE_SIZE);
      } catch (err) {
        console.error('[useRealtimeDirectMessages] Unexpected error:', err);
        setError('Failed to load messages');
      } finally {
        setIsLoading(false);
      }
    };

    fetchMessages();
  }, [conversationId, userId, enabled]);

  // Subscribe to realtime changes
  useEffect(() => {
    if (!conversationId || !userId || !enabled) {
      return;
    }

    const channelName = `direct-messages:${conversationId}`;

    // Clean up existing channel
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
    }

    const channel = supabase
      .channel(channelName)
      .on<DirectMessage>(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'direct_messages',
          filter: `conversation_id=eq.${conversationId}`,
        },
        async (payload: RealtimePostgresInsertPayload<DirectMessage>) => {
          console.log('[useRealtimeDirectMessages] New message received:', payload);

          // Fetch the full message with sender info
          const { data: newMessage, error: fetchError } = await supabase
            .from('direct_messages')
            .select(`
              *,
              sender:profiles!direct_messages_sender_id_fkey(
                id,
                display_name,
                avatar_url
              )
            `)
            .eq('id', payload.new.id)
            .single();

          if (fetchError) {
            console.error('[useRealtimeDirectMessages] Error fetching new message:', fetchError);
            return;
          }

          setMessages((prev) => {
            // Avoid duplicates (in case we sent the message ourselves)
            if (prev.some((m) => m.id === newMessage.id)) {
              return prev;
            }
            return [...prev, newMessage as DirectMessageWithSender];
          });
        }
      )
      .on<DirectMessage>(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'direct_messages',
          filter: `conversation_id=eq.${conversationId}`,
        },
        (payload) => {
          console.log('[useRealtimeDirectMessages] Message updated:', payload);
          setMessages((prev) =>
            prev.map((m) =>
              m.id === payload.new.id
                ? { ...m, ...payload.new }
                : m
            )
          );
        }
      )
      .subscribe((status) => {
        console.log('[useRealtimeDirectMessages] Subscription status:', status);
        setIsConnected(status === 'SUBSCRIBED');
        if (status === 'CHANNEL_ERROR') {
          setError('Failed to connect to realtime updates');
        }
      });

    channelRef.current = channel;

    return () => {
      console.log('[useRealtimeDirectMessages] Cleaning up channel');
      supabase.removeChannel(channel);
      channelRef.current = null;
    };
  }, [conversationId, userId, enabled]);

  // Send a message
  const sendMessage = useCallback(
    async (
      content: string,
      attachmentUrl?: string,
      attachmentType?: DirectMessage['attachment_type']
    ): Promise<{ success: boolean; error?: string }> => {
      if (!conversationId || !userId) {
        return { success: false, error: 'Missing conversation or user ID' };
      }

      // Validate that either content or attachment is provided
      if (!content?.trim() && !attachmentUrl) {
        return { success: false, error: 'Message must have content or attachment' };
      }

      try {
        const { data: inserted, error: insertError } = await supabase
          .from('direct_messages')
          .insert({
            conversation_id: conversationId,
            sender_id: userId,
            content: content?.trim() || null,
            attachment_url: attachmentUrl || null,
            attachment_type: attachmentType || null,
          })
          .select('id')
          .single();

        if (insertError) {
          console.error('[useRealtimeDirectMessages] Send error:', insertError);

          // Check for blocked user error
          if (insertError.message?.includes('blocked')) {
            return { success: false, error: 'You cannot send messages to this user' };
          }

          return { success: false, error: 'Failed to send message' };
        }

        // Best-effort push notifications to other participants
        try {
          const { data: participants } = await supabase
            .from('conversation_participants')
            .select('user_id')
            .eq('conversation_id', conversationId);

          const recipients = (participants || [])
            .map((p) => p.user_id)
            .filter((id) => id && id !== userId);

          const preview = (content?.trim() || '').slice(0, 120);

          await Promise.all(
            recipients.map((recipientId) =>
              supabase.functions.invoke('send-push-notification', {
                body: {
                  message_id: inserted?.id,
                  conversation_id: conversationId,
                  sender_id: userId,
                  recipient_id: recipientId,
                  content_preview: preview,
                  has_attachment: !!attachmentUrl,
                },
              })
            )
          );
        } catch (pushErr) {
          console.warn('[useRealtimeDirectMessages] Push notification send failed (ignored):', pushErr);
        }

        return { success: true };
      } catch (err) {
        console.error('[useRealtimeDirectMessages] Unexpected send error:', err);
        return { success: false, error: 'Failed to send message' };
      }
    },
    [conversationId, userId]
  );

  // Load more messages (cursor-based pagination)
  const loadMoreMessages = useCallback(
    async (cursor: string) => {
      if (!conversationId || !hasMoreMessages) return;

      try {
        const { data, error: fetchError } = await supabase
          .from('direct_messages')
          .select(`
            *,
            sender:profiles!direct_messages_sender_id_fkey(
              id,
              display_name,
              avatar_url
            )
          `)
          .eq('conversation_id', conversationId)
          .lt('created_at', cursor)
          .order('created_at', { ascending: false })
          .order('id', { ascending: false })
          .limit(PAGE_SIZE);

        if (fetchError) {
          console.error('[useRealtimeDirectMessages] Load more error:', fetchError);
          return;
        }

        const olderMessages = (data || []).reverse() as DirectMessageWithSender[];
        setMessages((prev) => [...olderMessages, ...prev]);
        setHasMoreMessages((data?.length || 0) >= PAGE_SIZE);
      } catch (err) {
        console.error('[useRealtimeDirectMessages] Unexpected load more error:', err);
      }
    },
    [conversationId, hasMoreMessages]
  );

  return {
    messages,
    isConnected,
    isLoading,
    error,
    sendMessage,
    loadMoreMessages,
    hasMoreMessages,
  };
}
